/**
 * Dock Mission Planner — right-rail panel that owns mission CRUD, the
 * waypoint editor, and the action buttons that hand a planned mission
 * off to the operator.
 *
 * Three sections:
 *   1. Mission list — in-memory CRUD over the project's missions
 *   2. Mission editor — name, description, global params, waypoints,
 *                       cost chips, scan wizard, action buttons
 *   3. Mission data import — Drive folder ingest of post-flight photos
 *                            + SRT flight path
 *
 * Action buttons in the editor:
 *   - Place waypoints (toggles map placement mode)
 *   - Draw scan area / Draw inspection line (scan-pattern wizard)
 *   - Export KMZ (F-03E-4: backend export via dockApi.exportDockMissionKmz;
 *                 the legacy client-side utils/wpml.downloadKmz is no longer
 *                 wired here)
 *   - Send to operator (queues mission for the dispatched operator)
 *   - Import mission data (opens the Drive ingest modal)
 *   - Live view (telemetry HUD over the planned route while flying)
 *
 * Mounts DemoMissionMapLayer as a child so the panel owns its on-map render.
 */

import { useState } from 'react';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import { useUIStore } from '../../stores';
import {
  exportDockMissionKmz,
  isDock3ExportGatedError,
  ApiError,
  type DockMissionExportResult,
} from '../../services/dockApi';
import WaypointEditorDemo from './WaypointEditorDemo';
import MissionLegEditor from './MissionLegEditor';
import MissionCostChips from './MissionCostChips';
import DriveMissionImportModalDemo from './DriveMissionImportModalDemo';
import Collapsible from './Collapsible';
import { useDemoWaypointPlacement } from './useDemoWaypointPlacement';
import { useDemoScanDraw } from './useDemoScanDraw';
import { listMissionPresets } from '../../stores/dockDemoStore';
import { validateMission } from '../../utils/missionValidation';
import type {
  DemoMissionFinishAction,
  DemoHeadingMode,
  DemoMissionFlowState,
  DemoDockStation,
  SavedDockSnapshot,
} from './types';
import './DockMissionDemoPanel.css';

/**
 * Long technical strings (raw `ApiError.message`, network stacks, etc.)
 * from `missionLastErrorById` are NOT user-facing copy. The UI surfaces a
 * generic banner; this helper exists for callers that want to *log* the
 * detail or render a sanitized short reason. Truncates aggressively so
 * even if a future store change leaks a stack the chip stays small.
 */
function sanitizeSaveErrorDetail(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.replace(/\s+/g, ' ').trim();
  if (trimmed.length === 0) return null;
  return trimmed.length > 80 ? trimmed.slice(0, 77) + '…' : trimmed;
}

/**
 * F-03E-4: build a safe KMZ filename for the browser download. Strips
 * filesystem-hostile characters so a maliciously-named mission can't
 * end up triggering download path tricks; falls back to the mission id.
 */
function buildKmzFilename(missionName: string, missionId: string): string {
  const cleaned = missionName.trim().replace(/[^A-Za-z0-9._-]+/g, '_');
  const safe = cleaned.length > 0 ? cleaned : missionId;
  return `${safe}.kmz`;
}

/**
 * F-03E-4: format `kmz_size_bytes` for the success metadata line. Uses
 * KB at <1 MiB and MB above; clamps to one decimal place so the chip
 * stays compact.
 */
function formatKmzSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * F-03E-4: trigger a same-tab download for a signed R2 KMZ URL. Uses an
 * anchor click rather than `window.open` so the browser honors the
 * `download` attribute and the user stays on the planner. Inline so it's
 * trivially mock-spyable in tests without exposing a helper module.
 */
function triggerKmzDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * F-03J-1 — predicate: a SavedDockSnapshot's coordinates are usable for
 * export. Mirrors the same finite/range checks the backend WPML
 * generator's `buildTakeoffRefBlock` performs (lng/lat must be finite
 * numbers in their valid ranges). The 0,0 check matches the
 * `'unlocated'` rule already used for live docks — a dock entity with
 * lat/lng both literally 0 is the convention for "registered but no
 * home point set yet" and cannot be flown.
 */
function isValidSnapshotCoords(snap: SavedDockSnapshot | undefined): boolean {
  if (!snap) return false;
  const { lng, lat } = snap;
  if (typeof lng !== 'number' || typeof lat !== 'number') return false;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  if (lng < -180 || lng > 180) return false;
  if (lat < -90 || lat > 90) return false;
  if (lng === 0 && lat === 0) return false;
  return true;
}

/**
 * F-03J-1 — synthesize a minimal `DemoDockStation`-shaped object from a
 * persisted snapshot for the purpose of running `validateMission`
 * (geofence containment, NFZ collision). NEVER added to `dockStations`
 * and NEVER persisted to localStorage — strictly an in-render
 * derivation. Snapshot fields propagate verbatim; fields the validator
 * doesn't read (drone, operator, serial, status, name) get placeholder
 * values. When the snapshot lacks a geofence, falls back to a
 * wide-radius "permissive" geofence so the validator doesn't crash and
 * the mission isn't artificially rejected — the operator-verification
 * contract in the export `warning_copy` still applies pre-flight.
 */
function synthesizeDockFromSnapshot(snap: SavedDockSnapshot): DemoDockStation {
  return {
    id: snap.dockStationId,
    name: '(saved snapshot)',
    serial: '',
    model: 'dock2',
    lng: snap.lng,
    lat: snap.lat,
    elevation: snap.elevationM,
    drone: { model: 'M3TD', payload: ['rgb'] },
    defaults: snap.defaults ?? {
      rthAltM: 100,
      maxAltM: 120,
      maxSpeedMps: 10,
      maxDistanceM: 1500,
    },
    // F-03J-1: when the snapshot lacks an explicit geofence, fall back
    // to a wide-radius "permissive" envelope so validation does not
    // block snapshot-only export. Operator verifies pre-flight per the
    // existing warning_copy contract.
    geofence: snap.geofence ?? { type: 'radius', radiusM: 50_000 },
    geofenceVisible: false,
    noFlyZones: snap.noFlyZones?.map((nfz) => ({
      id: nfz.id,
      name: nfz.name,
      vertices: nfz.vertices,
      bufferM: nfz.bufferM,
      visible: true,
    })),
    operator: {},
    createdAt: '',
    updatedAt: '',
  };
}

/** Small inline status pill in the missions list. Mirrors the flow chips
 *  shown by the in-map status badge for visual consistency. */
function FlowStateBadge({ state }: { state: DemoMissionFlowState }) {
  const label =
    state === 'pending_approval' ? 'pending' :
    state === 'scheduled' ? 'scheduled' :
    state === 'in_flight' ? 'in flight' :
    state === 'completed' ? 'done' :
    state;
  const cls =
    state === 'pending_approval' ? 'dock-demo-flow dock-demo-flow--pending' :
    state === 'scheduled' ? 'dock-demo-flow dock-demo-flow--scheduled' :
    state === 'in_flight' ? 'dock-demo-flow dock-demo-flow--inflight' :
    state === 'completed' ? 'dock-demo-flow dock-demo-flow--done' :
    'dock-demo-flow';
  return <span className={cls}>{label}</span>;
}

const FINISH_ACTIONS: DemoMissionFinishAction[] = ['goHome', 'noAction', 'autoLand'];
const HEADING_MODES: DemoHeadingMode[] = ['auto', 'fixed', 'followWayline'];

/**
 * Renders the dock mission planner *inside* the right-side ticket drawer.
 * The ticket frame (in EditorPage) provides the outer header + close button;
 * this component fills the body.
 */
export default function DockMissionDemoPanel() {
  const missions = useDockDemoStore((s) => s.missions);
  const selectedMissionId = useDockDemoStore((s) => s.selectedMissionId);
  const placementMode = useDockDemoStore((s) => s.placementMode);
  // Mission-data chips show the SELECTED mission's import counts (since
  // imports are now per-mission). When no mission is selected, all three
  // are zero/null and the empty-state copy renders.
  const importedPhotosCount = useDockDemoStore((s) => {
    const m = s.missions.find((x) => x.id === s.selectedMissionId);
    return m?.importedPhotos?.length ?? 0;
  });
  const importedFlightPath = useDockDemoStore((s) => {
    const m = s.missions.find((x) => x.id === s.selectedMissionId);
    return m?.importedFlightPath ?? null;
  });
  const importedVideosCount = useDockDemoStore((s) => {
    const m = s.missions.find((x) => x.id === s.selectedMissionId);
    return m?.importedVideos?.length ?? 0;
  });
  const dockStations = useDockDemoStore((s) => s.dockStations);

  // F-03E-3: server-backed mission state surfacing.
  // missionsLoading / missionsLoaded / missionsError drive the panel-level
  // load banner + the list "Loading…" vs "No saved dock missions yet"
  // distinction. The four per-mission dictionaries (`missionSavingById`,
  // `missionPendingSaveById`, `missionConflictById`, `missionLastErrorById`)
  // back the active-mission save status chips and the small list-item
  // indicators. All values come straight from F-03E-2's store — no
  // network calls happen in this panel.
  const missionsLoading = useDockDemoStore((s) => s.missionsLoading);
  const missionsLoaded = useDockDemoStore((s) => s.missionsLoaded);
  const missionsError = useDockDemoStore((s) => s.missionsError);
  const missionSavingById = useDockDemoStore((s) => s.missionSavingById);
  const missionPendingSaveById = useDockDemoStore((s) => s.missionPendingSaveById);
  const missionConflictById = useDockDemoStore((s) => s.missionConflictById);
  const missionLastErrorById = useDockDemoStore((s) => s.missionLastErrorById);

  // F-03E-4: backend export needs the mission row's siteId + current
  // server-tracked version for the If-Match header. Both come straight
  // from F-03E-2's store with no mutation here.
  const currentSiteId = useDockDemoStore((s) => s.currentSiteId);
  const missionVersions = useDockDemoStore((s) => s.missionVersions);

  const createMission = useDockDemoStore((s) => s.createMission);
  const createMissionFromPreset = useDockDemoStore((s) => s.createMissionFromPreset);
  const deleteMission = useDockDemoStore((s) => s.deleteMission);
  const selectMission = useDockDemoStore((s) => s.selectMission);
  const updateMission = useDockDemoStore((s) => s.updateMission);
  const setPlacementMode = useDockDemoStore((s) => s.setPlacementMode);
  const scanDrawMode = useDockDemoStore((s) => s.scanDrawMode);
  const scanDrawVertices = useDockDemoStore((s) => s.scanDrawVertices);
  const scanLineSpacingM = useDockDemoStore((s) => s.scanLineSpacingM);
  const scanLineStepM = useDockDemoStore((s) => s.scanLineStepM);
  const setScanDrawMode = useDockDemoStore((s) => s.setScanDrawMode);
  const setScanLineSpacingM = useDockDemoStore((s) => s.setScanLineSpacingM);
  const setScanLineStepM = useDockDemoStore((s) => s.setScanLineStepM);
  const clearScanDraft = useDockDemoStore((s) => s.clearScanDraft);
  const clearAll = useDockDemoStore((s) => s.clearAll);
  const openLiveView = useDockDemoStore((s) => s.openLiveView);
  const sendMissionToOperator = useDockDemoStore((s) => s.sendMissionToOperator);
  const cancelMissionFlow = useDockDemoStore((s) => s.cancelMissionFlow);

  const addNotification = useUIStore((s) => s.addNotification);
  const showConfirm = useUIStore((s) => s.showConfirm);
  const hideConfirm = useUIStore((s) => s.hideConfirm);

  const [showImportModal, setShowImportModal] = useState(false);

  // F-03E-4: backend KMZ export status. Kept local to the panel — the
  // F-03 backend's `kmz_*` columns are mission-row metadata that F-03E-2
  // does not (yet) project into the store, so we hold the last successful
  // export response here for the success-state UI. Cleared when the user
  // selects a different mission.
  const [exportInProgress, setExportInProgress] = useState(false);
  const [exportError, setExportError] = useState<
    | { code: 'dock3_gated'; message: string }
    | { code: 'conflict'; message: string }
    | { code: 'no_version'; message: string }
    | { code: 'no_site'; message: string }
    | { code: 'generic'; message: string; detail?: string }
    | null
  >(null);
  const [exportResult, setExportResult] = useState<DockMissionExportResult | null>(
    null,
  );
  const [exportResultMissionId, setExportResultMissionId] = useState<string | null>(
    null,
  );

  // Wire the placement hook + map render. These are render-only side effects;
  // they install/uninstall when the panel mounts/unmounts.
  useDemoWaypointPlacement();
  useDemoScanDraw();

  const selected = missions.find((m) => m.id === selectedMissionId) ?? null;

  // Lazy resolution: a mission's `dockStationId` may point at a deleted dock.
  // We never auto-clear the field on deletion (preserves user intent if they
  // recreate it); instead we resolve at every read. `boundDock` is the actual
  // dock object, `dockBindingState` is one of:
  //   - 'unbound'       : mission.dockStationId is null/undefined
  //   - 'missing'       : id is set but no live dock matches AND no valid
  //                       saved snapshot (truly orphaned binding)
  //   - 'unlocated'     : live dock exists but lng/lat are still 0,0 — can't
  //                       fly yet
  //   - 'snapshot-only' : F-03J-1 — no live dock locally but
  //                       `savedDockSnapshot` carries valid lng/lat.
  //                       Backend export already consumes the persisted
  //                       snapshot for takeOffRefPoint / RTH precedence;
  //                       UI allows export and surfaces an info hint.
  //   - 'ok'            : live dock exists with valid coords
  const boundDock =
    selected && selected.dockStationId
      ? dockStations.find((d) => d.id === selected.dockStationId) ?? null
      : null;
  const savedSnapshotValid =
    !!selected && isValidSnapshotCoords(selected.savedDockSnapshot);
  const dockBindingState: 'unbound' | 'missing' | 'unlocated' | 'snapshot-only' | 'ok' =
    !selected
      ? 'unbound'
      : !selected.dockStationId
        ? 'unbound'
        : boundDock
          ? boundDock.lng === 0 && boundDock.lat === 0
            ? 'unlocated'
            : 'ok'
          : savedSnapshotValid
            ? 'snapshot-only'
            : 'missing';
  // F-03J-1: validation must keep firing geofence/NFZ checks in
  // snapshot-only mode. Synthesize a minimal `DemoDockStation` from the
  // snapshot — NEVER pushed into the `dockStations` store, never
  // persisted, and never visible in any dropdown; strictly an in-render
  // derivation handed to `validateMission`.
  const dockForValidation: DemoDockStation | null =
    dockBindingState === 'snapshot-only' && selected?.savedDockSnapshot
      ? synthesizeDockFromSnapshot(selected.savedDockSnapshot)
      : boundDock;
  // Validation gate — block KMZ export and operator-dispatch when leg
  // params would generate an invalid WPML (lens conflicts, etc.). The
  // failing legs are surfaced inline on the leg cards; the button tooltip
  // gives a high-level reason so the user knows which control to fix.
  const validationErrors = selected ? validateMission(selected, dockForValidation) : [];
  const hasValidationErrors = validationErrors.length > 0;
  const canExport =
    !!selected
    && selected.waypoints.length > 0
    && (dockBindingState === 'ok' || dockBindingState === 'snapshot-only')
    && !hasValidationErrors;
  const exportTooltip =
    !selected ? '' :
    selected.waypoints.length === 0 ? 'Add at least one waypoint' :
    dockBindingState === 'unbound' ? 'Bind a dock first' :
    dockBindingState === 'missing' ? 'Bound dock no longer exists — pick another' :
    dockBindingState === 'unlocated' ? 'Bound dock has no location set yet' :
    hasValidationErrors ? `Fix ${validationErrors.length} validation error${validationErrors.length > 1 ? 's' : ''} below` :
    dockBindingState === 'snapshot-only' ? 'Download KMZ archive (using saved dock snapshot)' :
    'Download KMZ archive';

  // F-03E-4: backend-export-specific gates. The Export KMZ button is the
  // ONLY action subject to these; Send-to-operator / Live view continue to
  // use the shared `canExport` since they are demo-only and indifferent to
  // server version / dock-model availability.
  const selectedMissionVersion =
    selected ? (missionVersions[selected.id] ?? null) : null;
  const isDock3 = selected?.targetDockModel === 'dock3';
  const exportBlockedReason: 'no_selection' | 'no_site' | 'no_version' | 'dock3' | 'precheck' | 'in_progress' | null =
    !selected ? 'no_selection' :
    !currentSiteId ? 'no_site' :
    isDock3 ? 'dock3' :
    selectedMissionVersion === null ? 'no_version' :
    !canExport ? 'precheck' :
    exportInProgress ? 'in_progress' :
    null;
  const canCallBackendExport = exportBlockedReason === null;
  const exportButtonTooltip =
    exportBlockedReason === 'no_selection' ? '' :
    exportBlockedReason === 'no_site' ? 'Select a site before exporting.' :
    exportBlockedReason === 'dock3' ? 'Dock 3 KMZ export is not available yet. DJI Matrice 4D / 4TD WPML values are pending official confirmation.' :
    exportBlockedReason === 'no_version' ? 'Mission must be saved before export.' :
    exportBlockedReason === 'precheck' ? exportTooltip :
    exportBlockedReason === 'in_progress' ? 'Exporting…' :
    'Download KMZ from server';

  const handleNewMission = () => {
    const id = createMission('Mission ' + (missions.length + 1));
    selectMission(id);
  };

  const handleDeleteMission = (id: string, name: string) => {
    showConfirm({
      title: 'Delete mission?',
      message: `"${name}" and its waypoints will be removed.`,
      variant: 'danger',
      confirmLabel: 'Delete',
      onConfirm: () => {
        deleteMission(id);
        hideConfirm();
      },
    });
  };

  /**
   * F-03E-4: backend KMZ export.
   *
   * Calls `exportDockMissionKmz(siteId, missionId, version)` with the
   * If-Match version tracked by F-03E-2's store. On success, triggers a
   * same-tab anchor click against the signed `download_url`, surfaces the
   * backend's `warning_copy` verbatim, and renders the response's
   * `kmz_version` / `kmz_generated_at` / `kmz_size_bytes` metadata.
   *
   * Error paths (in priority order):
   *  - Dock 3 mission: blocked client-side via `canCallBackendExport`; if
   *    the call still happens, `isDock3ExportGatedError` catches the
   *    canonical 422 and renders the same gated copy (defense-in-depth).
   *  - 409 conflict: render conflict copy; the store will already have
   *    reloaded the mission via its own PATCH/GET conflict flow if the
   *    user runs a save next. Export does NOT bump mission version, so
   *    we do not mutate `missionVersions` here.
   *  - generic failure: render "Could not export KMZ. Try again." Raw
   *    `ApiError.message` / signed URL / KMZ R2 key / stack frames are
   *    NEVER displayed in the visible chip body.
   */
  const handleExportKmz = async () => {
    if (!selected) return;
    setExportError(null);
    if (!currentSiteId) {
      setExportError({
        code: 'no_site',
        message: 'Select a site before exporting.',
      });
      return;
    }
    if (selected.targetDockModel === 'dock3') {
      setExportError({
        code: 'dock3_gated',
        message:
          'Dock 3 KMZ export is not available yet. DJI Matrice 4D / 4TD WPML values are pending official confirmation.',
      });
      return;
    }
    const version = missionVersions[selected.id];
    if (typeof version !== 'number') {
      setExportError({
        code: 'no_version',
        message: 'Mission must be saved before export.',
      });
      return;
    }
    setExportInProgress(true);
    setExportResult(null);
    setExportResultMissionId(null);
    try {
      const result = await exportDockMissionKmz(
        currentSiteId,
        selected.id,
        version,
      );
      setExportResult(result);
      setExportResultMissionId(selected.id);
      triggerKmzDownload(
        result.download_url,
        buildKmzFilename(selected.name, selected.id),
      );
      addNotification({
        type: 'success',
        title: 'KMZ ready',
        message: `${selected.name} — KMZ v${result.kmz_version} downloading.`,
      });
    } catch (err) {
      if (isDock3ExportGatedError(err)) {
        setExportError({
          code: 'dock3_gated',
          message:
            'Dock 3 KMZ export is not available yet. DJI Matrice 4D / 4TD WPML values are pending official confirmation.',
        });
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        setExportError({
          code: 'conflict',
          message:
            'Mission changed on the server. Reload the mission and try again.',
        });
        return;
      }
      const detail =
        err instanceof ApiError || err instanceof Error
          ? err.message
          : undefined;
      setExportError({
        code: 'generic',
        message: 'Could not export KMZ. Try again.',
        detail: sanitizeSaveErrorDetail(detail) ?? undefined,
      });
    } finally {
      setExportInProgress(false);
    }
  };

  const handleSendToOperator = () => {
    if (!selected) return;
    sendMissionToOperator(selected.id);
    addNotification({
      type: 'info',
      title: 'Sent to operator',
      message:
        'A share link has been emailed to the certified BVLOS operator. You will be notified when the flight is scheduled.',
      duration: 6000,
    });
  };

  const handleCancelMissionFlow = () => {
    if (!selected) return;
    cancelMissionFlow(selected.id);
  };

  const handleClearAll = () => {
    showConfirm({
      title: 'Clear all mission data?',
      message: 'Removes every mission, waypoint, and imported file for this project.',
      variant: 'danger',
      confirmLabel: 'Clear',
      onConfirm: () => {
        clearAll();
        hideConfirm();
      },
    });
  };

  return (
    <>
      {/* DemoMissionMapLayer + DemoPhotoPopup live at EditorPage level so
          imported photos and the photo viewer survive ticket close. The
          waypoint route is rendered by DockMissionRouteLayer (also at
          EditorPage level). This panel only owns the editor UI itself. */}
      {showImportModal && (
        <DriveMissionImportModalDemo onClose={() => setShowImportModal(false)} />
      )}

      <div className="dock-demo-ticket">
          {/* F-03E-3: panel-level hydration error banner. Generic copy — raw
              errors stay out of the UI per F-03E-3 sanitization rule. */}
          {missionsError && (
            <div
              role="alert"
              data-testid="missions-error-banner"
              style={{
                padding: '8px 10px',
                margin: '4px 0 8px',
                borderRadius: 4,
                background: 'rgba(251, 191, 36, 0.10)',
                color: '#fbbf24',
                fontSize: 'var(--sl-text-sm)',
                border: '1px solid rgba(251, 191, 36, 0.35)',
              }}
            >
              Could not load saved dock missions. Check your connection and try again.
            </div>
          )}

          {/* ── 1. Missions list ── */}
          <Collapsible
            title="Missions"
            action={
              <button
                type="button"
                className="dock-demo-btn dock-demo-btn--primary"
                onClick={handleNewMission}
              >
                + New mission
              </button>
            }
          >
            {/* One-click templates — each spawns a new mission with a pre-
                configured camera + speed + altitude, so the user just places
                waypoints and the inspection style is already set. The
                resulting mission's name uses the preset label so the list
                stays self-documenting. */}
            <div className="dock-demo-presets">
              <div className="dock-demo-presets__label">Start from a template</div>
              <div className="dock-demo-presets__row">
                {listMissionPresets().map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    className="dock-demo-btn dock-demo-btn--ghost"
                    title={preset.description}
                    onClick={() => createMissionFromPreset(preset.key)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {missions.length === 0 ? (
              // F-03E-3: distinguish "loading from server" from "loaded but
              // empty". Until hydration completes (or while it's running),
              // we never claim the list is empty.
              missionsLoading || !missionsLoaded ? (
                <p className="dock-demo-empty" data-testid="missions-loading">
                  Loading saved missions…
                </p>
              ) : (
                <p className="dock-demo-empty" data-testid="missions-empty-loaded">
                  No saved dock missions yet — click <strong>New mission</strong> or a template above to start planning.
                </p>
              )
            ) : (
              <ul className="dock-demo-list">
                {missions.map((m) => {
                  const isVisible = m.visible !== false;
                  const isDataVisible = m.importedDataVisible !== false;
                  const isCancellable =
                    m.flowState === 'pending_approval' || m.flowState === 'scheduled';
                  return (
                    <li
                      key={m.id}
                      className={`dock-demo-list__item ${m.id === selectedMissionId ? 'dock-demo-list__item--active' : ''}`}
                      onClick={() => selectMission(m.id)}
                    >
                      <label
                        className="dock-demo-list__vis"
                        onClick={(e) => e.stopPropagation()}
                        title={isVisible ? 'Hide route on map' : 'Show route on map'}
                      >
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={(e) => updateMission(m.id, { visible: e.target.checked })}
                          aria-label={`Toggle route visibility of ${m.name}`}
                        />
                        <span className="dock-demo-list__vis-glyph" aria-hidden="true">⌐</span>
                      </label>
                      <label
                        className="dock-demo-list__vis dock-demo-list__vis--data"
                        onClick={(e) => e.stopPropagation()}
                        title={isDataVisible ? 'Hide imported photos + flight path' : 'Show imported photos + flight path'}
                      >
                        <input
                          type="checkbox"
                          checked={isDataVisible}
                          onChange={(e) => updateMission(m.id, { importedDataVisible: e.target.checked })}
                          aria-label={`Toggle imported-data visibility of ${m.name}`}
                        />
                        <span className="dock-demo-list__vis-glyph" aria-hidden="true">◉</span>
                      </label>
                      <div className="dock-demo-list__main">
                        <span className="dock-demo-list__name">
                          {m.name}
                          {/* F-03E-3: list-item save indicators. Inline glyphs
                              so the row layout is unchanged. */}
                          {(missionSavingById[m.id]
                            || missionPendingSaveById[m.id]) && (
                            <span
                              data-testid={`mission-list-saving-${m.id}`}
                              title={
                                missionSavingById[m.id]
                                  ? 'Saving to server…'
                                  : 'Save pending — waiting for in-flight save to finish'
                              }
                              aria-label="Saving"
                              style={{
                                marginLeft: 6,
                                color: 'var(--text-muted)',
                                fontSize: 'var(--sl-text-xs)',
                              }}
                            >
                              ⟳
                            </span>
                          )}
                          {missionConflictById[m.id] && (
                            <span
                              data-testid={`mission-list-conflict-${m.id}`}
                              title="Server has a newer version. This mission was reloaded."
                              aria-label="Conflict reloaded from server"
                              style={{
                                marginLeft: 6,
                                color: '#fbbf24',
                                fontSize: 'var(--sl-text-xs)',
                              }}
                            >
                              ⚠
                            </span>
                          )}
                          {!missionSavingById[m.id]
                            && missionLastErrorById[m.id] && (
                              <span
                                data-testid={`mission-list-error-${m.id}`}
                                title="Latest changes are local only — server save failed."
                                aria-label="Save failed"
                                style={{
                                  marginLeft: 6,
                                  color: '#fbbf24',
                                  fontSize: 'var(--sl-text-xs)',
                                }}
                              >
                                ⛌
                              </span>
                            )}
                        </span>
                        <span className="dock-demo-list__meta">
                          {m.waypoints.length} waypoints · {m.targetDockModel}
                          {m.flowState && m.flowState !== 'draft' && (
                            <>
                              {' · '}
                              <FlowStateBadge state={m.flowState} />
                              {isCancellable && (
                                /* Tiny inline cancel — reachable without
                                   opening the editor. Stops propagation so
                                   it doesn't double as a row select. */
                                <button
                                  type="button"
                                  className="dock-demo-list__cancel"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelMissionFlow(m.id);
                                  }}
                                  title="Cancel pending operator dispatch"
                                  aria-label={`Cancel dispatch of ${m.name}`}
                                >
                                  ✕ cancel
                                </button>
                              )}
                            </>
                          )}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="dock-demo-list__del"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMission(m.id, m.name);
                        }}
                        title="Delete mission"
                      >
                        ×
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Collapsible>

          {/* ── 2. Mission editor ── */}
          {selected && (
            <Collapsible
              title="Editor"
              sub={`${selected.waypoints.length} pts`}
            >
              {/* F-03E-3: active-mission save status. Renders only when
                  there is something worth surfacing — idle/synced missions
                  add no noise. Conflict + last-error are emphasised so
                  the user notices that local edits may not be on the
                  server. */}
              {(() => {
                const saving = !!missionSavingById[selected.id];
                const pending = !!missionPendingSaveById[selected.id];
                const conflict = !!missionConflictById[selected.id];
                const lastErr = sanitizeSaveErrorDetail(
                  missionLastErrorById[selected.id],
                );
                if (!saving && !pending && !conflict && !lastErr) return null;
                return (
                  <div
                    role="status"
                    data-testid="mission-save-status"
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {saving && (
                      <span
                        className="dock-demo-chip"
                        data-testid="mission-saving"
                      >
                        Saving…
                      </span>
                    )}
                    {!saving && pending && (
                      <span
                        className="dock-demo-chip"
                        data-testid="mission-pending"
                      >
                        Save pending…
                      </span>
                    )}
                    {conflict && (
                      <span
                        className="dock-demo-chip dock-demo-chip--warn"
                        role="alert"
                        data-testid="mission-conflict"
                      >
                        Server has a newer version. This mission was reloaded.
                      </span>
                    )}
                    {lastErr && (
                      <span
                        className="dock-demo-chip dock-demo-chip--warn"
                        role="alert"
                        data-testid="mission-error"
                        title={lastErr}
                      >
                        Latest changes are local only. Server save failed.
                      </span>
                    )}
                  </div>
                );
              })()}

              <label className="dock-demo-label">
                Name
                <input
                  type="text"
                  className="dock-demo-input"
                  value={selected.name}
                  onChange={(e) => updateMission(selected.id, { name: e.target.value })}
                />
              </label>

              <label className="dock-demo-label">
                Description
                <input
                  type="text"
                  className="dock-demo-input"
                  value={selected.description ?? ''}
                  placeholder="Optional"
                  onChange={(e) => updateMission(selected.id, { description: e.target.value })}
                />
              </label>

              <label
                className="dock-demo-label"
                title="Google Drive folder containing this mission's captured data. The live-view feed plays the first video from the folder, and on mission completion the folder's photos + SRT flight path are auto-imported onto the map."
              >
                Mission data folder (Drive URL)
                <input
                  type="url"
                  className="dock-demo-input"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={selected.mockDataFolderUrl ?? ''}
                  onChange={(e) =>
                    updateMission(selected.id, {
                      mockDataFolderUrl: e.target.value || undefined,
                    })
                  }
                />
              </label>

              <label className="dock-demo-label">
                Dock
                <select
                  className="dock-demo-input"
                  value={selected.dockStationId ?? ''}
                  onChange={(e) =>
                    updateMission(selected.id, {
                      dockStationId: e.target.value === '' ? null : e.target.value,
                    })
                  }
                >
                  <option value="">— No dock bound —</option>
                  {dockStations.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name || '(unnamed dock)'}
                    </option>
                  ))}
                  {(dockBindingState === 'missing' || dockBindingState === 'snapshot-only') && (
                    // Surface the stale id so the user sees what was bound
                    // before; selecting a real dock above replaces it.
                    // F-03J-1: in snapshot-only mode the label reads
                    // "saved snapshot" instead of "deleted dock" to align
                    // with the info hint below.
                    <option value={selected.dockStationId ?? ''} disabled>
                      {dockBindingState === 'snapshot-only'
                        ? `(saved snapshot — id ${selected.dockStationId})`
                        : `(deleted dock — id ${selected.dockStationId})`}
                    </option>
                  )}
                </select>
                {dockBindingState === 'missing' && (
                  <span className="dock-demo-hint dock-demo-hint--warn">
                    The dock previously bound to this mission no longer exists. Pick another to enable export.
                  </span>
                )}
                {dockBindingState === 'snapshot-only' && (
                  // F-03J-1: info-level — Export remains enabled because
                  // backend export already consumes the persisted
                  // `plan_json.dockSnapshot` for takeOffRefPoint / RTH.
                  // Distinct from `--warn` styling so the user knows the
                  // mission can still be exported on this device.
                  <span className="dock-demo-hint" data-testid="dock-snapshot-info">
                    Using saved dock snapshot from last save. The live dock record is unavailable on this device.
                  </span>
                )}
                {dockBindingState === 'unbound' && dockStations.length === 0 && (
                  <span className="dock-demo-hint">
                    No docks registered yet — open <strong>Dock settings</strong> on the right rail.
                  </span>
                )}
                {dockBindingState === 'unlocated' && (
                  <span className="dock-demo-hint dock-demo-hint--warn">
                    Bound dock has no location set yet — set its home point in <strong>Dock settings</strong>.
                  </span>
                )}
              </label>

              <div className="dock-demo-grid-3">
                <label className="dock-demo-label dock-demo-label--inline">
                  Speed (m/s)
                  <input
                    type="number"
                    min={1}
                    max={15}
                    className="dock-demo-input"
                    value={selected.globalSpeedMps}
                    onChange={(e) =>
                      updateMission(selected.id, { globalSpeedMps: Number(e.target.value) || 1 })
                    }
                  />
                </label>
                <label className="dock-demo-label dock-demo-label--inline">
                  Finish
                  <select
                    className="dock-demo-input"
                    value={selected.finishAction}
                    onChange={(e) =>
                      updateMission(selected.id, {
                        finishAction: e.target.value as DemoMissionFinishAction,
                      })
                    }
                  >
                    {FINISH_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </label>
                <label className="dock-demo-label dock-demo-label--inline">
                  Heading
                  <select
                    className="dock-demo-input"
                    value={selected.headingMode}
                    onChange={(e) =>
                      updateMission(selected.id, {
                        headingMode: e.target.value as DemoHeadingMode,
                      })
                    }
                  >
                    {HEADING_MODES.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>
              </div>

              {/* Cost / feasibility chips — time, distance, battery, photos,
                  status. Status mirrors the same `validationErrors` that
                  gates Send / Export above, so users see "why can't I send?"
                  at a glance without scrolling through every leg card. */}
              <MissionCostChips
                mission={selected}
                dock={boundDock}
                validationErrors={validationErrors}
              />

              {/* Self-clearing cleanup for missions persisted from an earlier
                  build that included the (now-removed) auto-router. Only
                  rendered when at least one autoRouted waypoint is still on
                  the mission; vanishes after a successful click. */}
              {selected.waypoints.some((w) => w.autoRouted) && (
                <div className="dock-demo-actions">
                  <button
                    type="button"
                    className="dock-demo-btn dock-demo-btn--ghost"
                    onClick={() => {
                      const n = useDockDemoStore.getState().removeAutoRoutedWaypoints(selected.id);
                      if (n > 0) {
                        addNotification({
                          type: 'success',
                          title: 'Auto-routed waypoints removed',
                          message: `Stripped ${n} waypoint${n > 1 ? 's' : ''} from a previous auto-route run.`,
                        });
                      }
                    }}
                    title="One-time cleanup: remove waypoints inserted by the now-removed auto-router"
                  >
                    Clear auto-routed waypoints
                  </button>
                </div>
              )}

              {/* View settings — currently just the camera-coverage toggle,
                  surfaced inline so the user can hide the translucent blue
                  footprints if they obscure the basemap or route they're
                  reviewing. Per-mission so different missions can have
                  different visualizations open. */}
              <div className="dock-demo-view-row">
                <label className="dock-demo-checkbox">
                  <input
                    type="checkbox"
                    checked={selected.coverageVisible !== false}
                    onChange={(e) =>
                      updateMission(selected.id, { coverageVisible: e.target.checked })
                    }
                  />
                  <span>Show camera coverage</span>
                </label>
              </div>

              {/* Scan-pattern wizard — bulk-place waypoints in one of two
                  patterns:
                    • Scan area  : polygon-fill serpentine, default 15 m
                                   line spacing. Enter to finalize.
                    • Inspection line : 2-click line, default 10 m steps.
                                        Auto-finalizes on the second click.
                  Esc cancels either mode. While drawing, the button label
                  shows the live vertex count so the user knows the gesture
                  state without looking at the map. */}
              <div className="dock-demo-actions">
                <button
                  type="button"
                  className={`dock-demo-btn ${scanDrawMode === 'polygon' ? 'dock-demo-btn--active' : ''}`}
                  onClick={() =>
                    setScanDrawMode(scanDrawMode === 'polygon' ? null : 'polygon')
                  }
                  title="Draw a polygon to fill with a back-and-forth sweep of waypoints"
                >
                  {scanDrawMode === 'polygon'
                    ? `✓ Drawing area (${scanDrawVertices.length} vertices — Enter to finish)`
                    : 'Draw scan area'}
                </button>
                <button
                  type="button"
                  className={`dock-demo-btn ${scanDrawMode === 'line' ? 'dock-demo-btn--active' : ''}`}
                  onClick={() =>
                    setScanDrawMode(scanDrawMode === 'line' ? null : 'line')
                  }
                  title="Click two points to place a row of waypoints along the segment"
                >
                  {scanDrawMode === 'line'
                    ? `✓ Drawing line (${scanDrawVertices.length}/2)`
                    : 'Draw inspection line'}
                </button>
                {scanDrawMode !== null && (
                  <button
                    type="button"
                    className="dock-demo-btn dock-demo-btn--ghost"
                    onClick={clearScanDraft}
                    title="Cancel the wizard without inserting waypoints"
                  >
                    Cancel
                  </button>
                )}
              </div>

              {/* Density / step control — only visible while drawing. The
                  three preset chips are syntactic sugar over the numeric
                  input (same store field); user can dial in any value
                  between 3 m and 80 m via the input directly. Polygon mode
                  shows sweep-line spacing; line mode shows the along-line
                  step distance. */}
              {scanDrawMode !== null && (
                <div className="dock-demo-scan-density">
                  <label className="dock-demo-scan-density__label">
                    <span>
                      {scanDrawMode === 'polygon' ? 'Line spacing' : 'Step'} (m)
                    </span>
                    <input
                      type="number"
                      min={scanDrawMode === 'polygon' ? 3 : 2}
                      max={80}
                      step={1}
                      value={
                        scanDrawMode === 'polygon' ? scanLineSpacingM : scanLineStepM
                      }
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if (scanDrawMode === 'polygon') setScanLineSpacingM(v);
                        else setScanLineStepM(v);
                      }}
                    />
                  </label>
                  <div className="dock-demo-scan-density__presets">
                    {(scanDrawMode === 'polygon'
                      ? [
                          { label: 'Coarse', value: 25 },
                          { label: 'Medium', value: 15 },
                          { label: 'Fine', value: 8 },
                        ]
                      : [
                          { label: 'Coarse', value: 20 },
                          { label: 'Medium', value: 10 },
                          { label: 'Fine', value: 5 },
                        ]
                    ).map((p) => {
                      const active =
                        scanDrawMode === 'polygon'
                          ? scanLineSpacingM === p.value
                          : scanLineStepM === p.value;
                      return (
                        <button
                          key={p.label}
                          type="button"
                          className={`dock-demo-scan-density__chip${
                            active ? ' is-active' : ''
                          }`}
                          onClick={() => {
                            if (scanDrawMode === 'polygon') setScanLineSpacingM(p.value);
                            else setScanLineStepM(p.value);
                          }}
                          title={`${p.label} — ${p.value} m`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="dock-demo-actions">
                <button
                  type="button"
                  className={`dock-demo-btn ${placementMode ? 'dock-demo-btn--active' : ''}`}
                  onClick={() => setPlacementMode(!placementMode)}
                >
                  {placementMode ? '✓ Placing — click map' : 'Place waypoints'}
                </button>
                <button
                  type="button"
                  className="dock-demo-btn"
                  data-testid="export-kmz-button"
                  onClick={handleExportKmz}
                  disabled={!canCallBackendExport}
                  title={exportButtonTooltip}
                >
                  {exportInProgress ? 'Exporting KMZ…' : 'Export KMZ'}
                </button>
                {selected.flowState === 'pending_approval' || selected.flowState === 'scheduled' ? (
                  // While a flow is active, swap "Send" for "Cancel" so the
                  // user can abort before the auto-advance fires. The status
                  // badge on the map shows the live countdown / pending tag.
                  <button
                    type="button"
                    className="dock-demo-btn dock-demo-btn--ghost"
                    onClick={handleCancelMissionFlow}
                    title="Cancel pending operator dispatch"
                  >
                    Cancel dispatch
                  </button>
                ) : (
                  <button
                    type="button"
                    className="dock-demo-btn"
                    onClick={handleSendToOperator}
                    disabled={!canExport || selected.flowState === 'in_flight'}
                    title={
                      selected.flowState === 'in_flight'
                        ? 'Mission is currently in flight — re-dispatch is not available until live view closes'
                        : selected.flowState === 'completed'
                          ? 'Re-dispatch this mission for another flight'
                          : exportTooltip
                    }
                  >
                    {selected.flowState === 'completed' ? 'Re-send to operator' : 'Send to operator'}
                  </button>
                )}
                <button
                  type="button"
                  className="dock-demo-btn"
                  onClick={() => selected && openLiveView(selected.id)}
                  disabled={!canExport}
                  title={
                    !canExport
                      ? exportTooltip
                      : 'Open live view — telemetry HUD over the planned route'
                  }
                >
                  Live view
                </button>
              </div>

              {/* F-03E-4: backend KMZ export status. Renders only when there
                  is something to show — in-flight indicator, the last
                  successful response's warning_copy + compact metadata, or
                  the most recent typed error. Idle exports add no noise. */}
              {(exportInProgress
                || exportError
                || (exportResult && exportResultMissionId === selected.id)) && (
                <div
                  role="status"
                  data-testid="export-status"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    marginTop: 6,
                  }}
                >
                  {exportInProgress && (
                    <span
                      className="dock-demo-chip"
                      data-testid="export-in-progress"
                    >
                      Exporting KMZ…
                    </span>
                  )}
                  {!exportInProgress
                    && exportResult
                    && exportResultMissionId === selected.id && (
                      <>
                        <p
                          className="dock-demo-hint"
                          data-testid="export-warning-copy"
                          style={{ margin: 0 }}
                        >
                          {exportResult.warning_copy}
                        </p>
                        <div
                          data-testid="export-metadata"
                          style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
                        >
                          <span
                            className="dock-demo-chip"
                            data-testid="export-kmz-version"
                          >
                            KMZ v{exportResult.kmz_version}
                          </span>
                          <span
                            className="dock-demo-chip"
                            data-testid="export-kmz-generated-at"
                          >
                            generated {exportResult.kmz_generated_at}
                          </span>
                          <span
                            className="dock-demo-chip"
                            data-testid="export-kmz-size"
                          >
                            {formatKmzSize(exportResult.kmz_size_bytes)}
                          </span>
                        </div>
                      </>
                    )}
                  {!exportInProgress && exportError && (
                    <span
                      className="dock-demo-chip dock-demo-chip--warn"
                      role="alert"
                      data-testid={`export-error-${exportError.code}`}
                      title={
                        exportError.code === 'generic'
                          ? exportError.detail
                          : undefined
                      }
                    >
                      {exportError.message}
                    </span>
                  )}
                </div>
              )}

              {/* F-03E-3: Phase 2 / demo disclosure. "Send to operator" and
                  "Live view" remain visible (per feedback rule that WIP
                  features stay visible by default) but their non-operational
                  nature is called out below the buttons. */}
              <p
                className="dock-demo-hint"
                data-testid="phase-2-disclosure"
                style={{ marginTop: 4 }}
              >
                Phase 2 preview — &ldquo;Send to operator&rdquo; and &ldquo;Live view&rdquo; are demo only: no operator dispatch is sent and no dock telemetry is connected.
              </p>

              {/* F-03E-3: Dock 3 export-gated note. Planning + persistence
                  for Dock 3 missions is supported by F-03B/F-03E-2; the
                  KMZ export endpoint returns 422 DOCK3_EXPORT_NOT_YET_SUPPORTED
                  until DJI publishes Matrice 4D / 4TD WPML enum values. */}
              {selected.targetDockModel === 'dock3' && (
                <p
                  className="dock-demo-hint dock-demo-hint--warn"
                  data-testid="dock3-export-gated-note"
                  style={{ marginTop: 4 }}
                >
                  Dock 3 missions can be planned and saved now; KMZ export is gated until DJI publishes Matrice 4D / 4TD WPML values.
                </p>
              )}

              <WaypointEditorDemo mission={selected} />
              <MissionLegEditor mission={selected} />
            </Collapsible>
          )}

          {/* ── 3. Mission data import ── */}
          <Collapsible
            title="Mission data"
            action={
              <button
                type="button"
                className="dock-demo-btn"
                onClick={() => setShowImportModal(true)}
                disabled={!selected}
                title={selected ? 'Import a Drive folder into this mission' : 'Select a mission first'}
              >
                Import from Drive…
              </button>
            }
          >
            {!selected ? (
              <p className="dock-demo-empty">
                Select a mission above — imported data is attached to a mission so its visibility checkbox controls the map.
              </p>
            ) : importedPhotosCount === 0 && !importedFlightPath ? (
              <p className="dock-demo-empty">
                No imported data for <strong>{selected.name}</strong>. Import a Drive folder to drop example photos and a flight path on the map.
              </p>
            ) : (
              <div className="dock-demo-import-summary">
                {importedPhotosCount > 0 && (
                  <div className="dock-demo-chip">{importedPhotosCount} photos</div>
                )}
                {importedVideosCount > 0 && (
                  <div className="dock-demo-chip">{importedVideosCount} videos</div>
                )}
                {importedFlightPath && (
                  <div className="dock-demo-chip">
                    flight path · {importedFlightPath.coordinates.length} pts
                  </div>
                )}
              </div>
            )}
          </Collapsible>

          {/* ── Footer ── */}
          <section className="dock-demo-section dock-demo-section--footer">
            <button
              type="button"
              className="dock-demo-btn dock-demo-btn--ghost"
              onClick={handleClearAll}
              disabled={missions.length === 0 && importedPhotosCount === 0 && !importedFlightPath}
            >
              Clear mission data
            </button>
          </section>
      </div>
    </>
  );
}
